import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { getInvoiceNumberLabel, matchesInvoiceNumberQuery } from '../utils/invoiceNumber';

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
};

function CustomerVehicleReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = () => {
    setLoading(true);
    axios
      .get('https://serverdemo.ccsoftwares.com/billing/customerInvoices')
      .then((res) => {
        setRows(res.data || []);
      })
      .catch(() => {
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.Customer_name || '').toLowerCase().includes(s) ||
        (r.telephone || '').toLowerCase().includes(s) ||
        (r.Vehicle_no || '').toLowerCase().includes(s) ||
        matchesInvoiceNumberQuery(r.inv_no, s)
    );
  }, [rows, search]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Customer & Vehicle Report
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            <TextField
              label="Search by customer, vehicle or invoice"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              variant="outlined"
              size="small"
              sx={{ minWidth: 280 }}
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Vehicle No</TableCell>
                      <TableCell>Invoice No</TableCell>
                      <TableCell>Bill Date</TableCell>
                      <TableCell>Created On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row, idx) => (
                        <TableRow key={`${row.cikey || idx}-${row.inv_no || idx}`}>
                          <TableCell>{row.Customer_name || '-'}</TableCell>
                          <TableCell>{row.telephone || '-'}</TableCell>
                          <TableCell>{row.Vehicle_no || '-'}</TableCell>
                          <TableCell>{getInvoiceNumberLabel(row.inv_no) || '-'}</TableCell>
                          <TableCell>{row.Bill_date || '-'}</TableCell>
                          <TableCell>{formatDateTime(row.creation_date)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default CustomerVehicleReport;
